import styled from "@emotion/styled"
import { useRef, useLayoutEffect } from "react"
import { useConsole } from "contexts/Console"
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useScroll,
} from "framer-motion"

import { useMoonCalendar } from "./context"

const Main = styled.div`
  position: relative;
  grid-row: 2;
  grid-column: doc;
  height: 100%;
  width: 100%;
  pointer-events: none;
`

const Scrub = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;

  &.wd-touch {
    width: 300%;
    cursor: default;
  }

  @media (hover: hover) {
    cursor: pointer;
  }
`

const Drag = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  scroll-behavior: auto;
  overscroll-behavior-x: none;
  user-select: none;
  -webkit-user-select: none;
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;

  .mc-ended & {
    pointer-events: auto;
  }
`

function WDDrag(props) {
  const { ctx } = props
  const { moon_time, px_ratio, moving } = useMoonCalendar(ctx)

  const isDragging = useRef(false)
  const startX = useRef(0)
  const startMoonTime = useRef(0)
  const containerRect = useRef(null)

  const px = useMotionValue(0)

  // Sync with moon_time
  function onPx(v) {
    const value = startMoonTime.current - v * px_ratio.current
    if (isNaN(value)) return
    moon_time.set(value)
  }
  useMotionValueEvent(px, "change", onPx)

  // Completely stops the drag action
  function stopDragging(e) {
    if (!isDragging.current) return
    isDragging.current = false

    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch (err) { }
  }

  function onPointerDown(e) {
    isDragging.current = true
    startX.current = e.clientX || (e.touches && e.touches.clientX)
    startMoonTime.current = moon_time.get()

    // Save the exact bounding box of the element
    containerRect.current = e.currentTarget.getBoundingClientRect()

    // Locks the pointer to keep tracking correctly
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function onPointerMove(e) {
    if (!isDragging.current) return

    const currentX = e.clientX || (e.touches && e.touches.clientX)
    const currentY = e.clientY || (e.touches && e.touches.clientY)
    const rect = containerRect.current

    // CRITICAL CHECK: Cancel immediately if mouse coordinates go outside the box boundaries
    if (
      rect &&
      (currentX < rect.left ||
        currentX > rect.right ||
        currentY < rect.top ||
        currentY > rect.bottom)
    ) {
      stopDragging(e)
      return
    }

    moving.set(0)
    const deltaX = startX.current - currentX
    px.set(deltaX)
  }

  return (
    <Drag>
      <Scrub
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={stopDragging}
        tabIndex='-1'
      />
    </Drag>
  )
}

/*
function WDDrag(props) {
  const { ctx } = props
  const { moon_time, px_ratio, moving } = useMoonCalendar(ctx)
  const start = useRef(0)
  const px = useMotionValue(0)
  const panning = useRef(moon_time.get())

  function onTapStart() {
    start.current = Date.now()
    panning.current = moon_time.get()
    px.set(0)
  }

  function onTapCancel() {
    panning.current = moon_time.get()
  }

  function onPan(e, info) {
    moving.set(0)
    let dx = px.get() - info.delta.x
    px.set(dx)
  }

  function onPx(v) {
    const value = panning.current - v * px_ratio.current
    //    if (!value || isNaN(value) || value < 0) return
    if (!value || isNaN(value)) return
    moon_time.set(value)
  }
  useMotionValueEvent(px, "change", onPx)

  return (
    <Drag>
      <Scrub
        drag='x'
        dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
        dragTransition={{ bounceStiffness: 10000, bounceDamping: 10000 }}
        onPan={onPan}
        onPanEnd={onTapCancel}
        onTapStart={onTapStart}
        onTapCancel={onTapCancel}
        onTap={onTapCancel}
        tabIndex='-1'
      ></Scrub>
    </Drag>
  )
}
*/
function WDTouchDrag(props) {
  const { ctx } = props
  const { moon_time, px_ratio_p, moving } = useMoonCalendar(ctx)
  const px = useMotionValue(0)
  const panning = useRef(moon_time.get())
  const touched = useRef(false)

  const rfdrag = useRef()
  const { scrollXProgress } = useScroll({ container: rfdrag, offset: ["start end", "end end"] })

  function initDrag() {
    panning.current = moon_time.get()
    restoreDrag()
  }

  function clearDrag() {
    if (!touched.current) panning.current = moon_time.get()
    restoreDrag()
  }

  function restoreDrag() {
    rfdrag.current.scrollLeft = rfdrag.current.offsetWidth
  }

  function onScrollXProgress(v) {
    if (!touched.current) return
    moving.set(0)
    const offset = v - 0.5
    px.set(offset)
  }
  useMotionValueEvent(scrollXProgress, "change", onScrollXProgress)

  function onTouchStart() {
    touched.current = true
    initDrag()
    px.set(0)
  }

  function onTouchEnd() {
    touched.current = false
    clearDrag()
  }

  function onPx(v) {
    const value = panning.current - v * px_ratio_p.current
    //    if (!value || isNaN(value) || value < 0) return
    if (!value || isNaN(value)) return
    moon_time.set(value)
  }
  useMotionValueEvent(px, "change", onPx)

  useLayoutEffect(() => {
    restoreDrag()
  }, [])

  return (
    <Drag ref={rfdrag}>
      <Scrub className='wd-touch' onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} />
    </Drag>
  )
}

export default function MoonCalendarDrag(props) {
  const console = useConsole()
  const { ctx } = props
  const { touchenabled } = useMoonCalendar(ctx)

  return (
    <Main dir="ltr">
      {touchenabled === 1 ?
        <WDTouchDrag ctx={ctx} /> :
        touchenabled === 0 ?
          <WDDrag ctx={ctx} /> :
          null}
    </Main>
  )
}
